import { ReactNode, useEffect, useState } from 'react';

import { ProductType } from '~/core/enums';
import {
  ApplianceModel,
  ApplianceModelId,
  Filter,
  ProductDetails,
  SelectedItemProperties,
} from '~/core/models';
import { buildQteAttributes } from '~/core/utils';
import { createTypeSafeContext } from '~/core/utils';
import { updateFiltersOptions } from '~/core/utils/sortAndFilter';

type FaucetFilters = Record<string, Filter>;

export interface FaucetContextValue {
  faucets: ApplianceModel[];
  updateFaucets: (faucets: ApplianceModel[]) => void;
  selectedFaucets: Record<ApplianceModelId, SelectedItemProperties>;
  updateSelectedFaucets: (
    faucets: Record<ApplianceModelId, SelectedItemProperties>
  ) => void;
  faucetsToProductDetails: () => ProductDetails[];
  resetFaucets: () => void;
  isFilterExpanded: boolean;
  updateFiltersExpandState: (value: boolean) => void;
  faucetFilters: FaucetFilters;
  updateFaucetFilters: (filters: FaucetFilters) => void;
  restorableFaucets: Record<number, SelectedItemProperties>;
  getRestorableFaucets: () => Record<number, SelectedItemProperties>;
  updateRestorableFaucets: (
    restoreMap: Record<number, SelectedItemProperties>
  ) => void;
  resetRestorableFaucets: () => void;
}

export interface SelectedOptionalProductsProperties
  extends SelectedItemProperties {
  note?: string;
}

export const [useFaucetContext, FaucetsContext] =
  createTypeSafeContext<FaucetContextValue>();

export const FaucetContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const [faucets, setFaucets] = useState<ApplianceModel[]>([]);
  const [selectedFaucets, setSelectedFaucets] = useState<
    Record<ApplianceModelId, SelectedItemProperties>
  >({});
  const [isFilterExpanded, setFilterExpanded] = useState(false);
  const [faucetFilters, setFaucetFilters] = useState<FaucetFilters>({});
  const [restorableFaucets, setRestorableFaucets] = useState<
    Record<ApplianceModelId, SelectedItemProperties>
  >({});

  const updateFaucets = (newFaucets: ApplianceModel[]) => {
    if (Object.keys(restorableFaucets).length > 0) {
      restoreFaucets(newFaucets);
    }
    setFaucets(newFaucets);
  };

  useEffect(() => {
    if (!faucets) {
      return;
    }
    updateFiltersOptions(faucetFilters, faucets);
  }, [faucets, faucetFilters]);

  const updateFiltersExpandState = (value: boolean) => {
    setFilterExpanded(value);
  };

  const updateFaucetFilters = (faucetFilters: FaucetFilters) => {
    setFaucetFilters(faucetFilters);
  };

  const faucetsToProductDetails = (): ProductDetails[] => {
    if (!faucets) {
      return [];
    }

    return faucets.map((knownFaucet) => {
      const detail: ProductDetails = {
        productId: knownFaucet.productId,
        name: knownFaucet.name,
        consumerDesc: knownFaucet.consumerDesc,
        productType: ProductType.FAUCET,
        price: knownFaucet.price,
        priceWithoutDiscount: knownFaucet.priceWithoutDiscount,
        expirationPromotionDate: knownFaucet.expirationPromotionDate,
        abbrevDesc: knownFaucet.abbrevDesc,
        unitOfMeasure: knownFaucet.unitOfMeasure,
        ...buildQteAttributes(knownFaucet),
      };

      return detail;
    });
  };

  const restoreFaucets = (newFaucets: ApplianceModel[]) => {
    const clone = { ...restorableFaucets };

    const selectedMap: Record<number, SelectedItemProperties> = {};

    newFaucets.forEach((faucet) => {
      const restorable = clone[faucet.productId];

      if (restorable) {
        selectedMap[faucet.productId] = restorable;

        delete clone[faucet.productId];
      }
    });

    // Update state or pass these objects where needed
    setSelectedFaucets(selectedMap);
    setRestorableFaucets(clone);
  };

  const updateSelectedFaucets = (
    faucets: Record<number, SelectedItemProperties>
  ) => {
    setSelectedFaucets(faucets);
  };

  const resetFaucets = () => {
    setSelectedFaucets({});
    setFaucets([]);
  };

  const getRestorableFaucets = (): Record<number, SelectedItemProperties> => {
    const restorableFaucets: Record<number, SelectedItemProperties> = {};

    Object.entries(selectedFaucets).forEach(([key, value]) => {
      if (value.isSelected && value.quantity > 0) {
        restorableFaucets[Number(key)] = value;
      }
    });

    return restorableFaucets;
  };

  const updateRestorableFaucets = (
    restoreMap: Record<number, SelectedItemProperties>
  ) => {
    setRestorableFaucets(restoreMap);
  };

  const resetRestorableFaucets = () => {
    setRestorableFaucets({});
  };

  const faucetContextValue: FaucetContextValue = {
    faucets,
    updateFaucets,
    selectedFaucets,
    updateSelectedFaucets,
    faucetsToProductDetails,
    resetFaucets,
    isFilterExpanded,
    updateFiltersExpandState,
    faucetFilters,
    updateFaucetFilters,
    restorableFaucets,
    getRestorableFaucets,
    updateRestorableFaucets,
    resetRestorableFaucets,
  };

  return (
    <FaucetsContext.Provider value={faucetContextValue}>
      {children}
    </FaucetsContext.Provider>
  );
};
